// LES CHAMPS D'UNE MAINTENANCE QUI NE SE TAPENT PLUS LIBREMENT (RQ, 03/11/2026).
//
// Relevé sur les 59 interventions enregistrées : la durée d'arrêt s'écrivait
// « 4 JOURS », « 4 J », « 1/2 journée », « ss arret », « du 6 vers 25 MAI » ;
// la même membrane, « membrane de ref », « Membrane de référence », « mem de
// ref » ; « non » et « NON » pour « aucune pièce » ; et la vérification avant
// remise en service n'avait JAMAIS été remplie.
//
// Le STOCKAGE ne change pas : chaque champ reste un texte, écrit ici sous une
// forme régulière (« 4 jours », « Membrane de référence, Tuyau de pompe »,
// « CQI passés et conformes · Calibration refaite »). L'historique, l'export
// et les corrections tracées lisent donc toujours la même chose ; une valeur
// ancienne qui ne se relit pas est montrée telle quelle, jamais effacée.
//
// Module PUR : testé depuis `backend/tests/front-champs-maintenance.test.js`.

/* ─────────────────────────────── Durée d'arrêt ─────────────────────────── */

export type UniteDuree = 'h' | 'j';
export interface Duree { sansArret: boolean; nombre: string; unite: UniteDuree }

export const DUREE_VIDE: Duree = { sansArret: false, nombre: '', unite: 'h' };

const SANS_ARRET = /^(sans|ss|pas d['’]?|aucun)\s*arr[eê]t$|^0$/i;
const NOMBRE = '(\\d+(?:[.,]\\d+)?)';
const HEURES = new RegExp(`^${NOMBRE}\\s*(h|hr?s?|heures?)$`, 'i');
const JOURS = new RegExp(`^${NOMBRE}\\s*(j|jrs?|jours?|journ[ée]es?)$`, 'i');
const DEMI_JOURNEE = /^(1\/2|une demi|demi)[\s-]*journ[ée]e$/i;

/** Relit une durée enregistrée. `duree: null` = texte ancien qui ne se relit
 *  pas (« du 6 vers 25 MAI ») : l'écran le montre et le garde tant qu'on ne
 *  saisit rien d'autre. */
export function lireDuree(texte: string | null | undefined): { duree: Duree | null; brut: string } {
  const brut = String(texte ?? '').trim();
  if (!brut) return { duree: { ...DUREE_VIDE }, brut: '' };
  if (SANS_ARRET.test(brut)) return { duree: { sansArret: true, nombre: '', unite: 'h' }, brut };
  if (DEMI_JOURNEE.test(brut)) return { duree: { sansArret: false, nombre: '0,5', unite: 'j' }, brut };
  const h = brut.match(HEURES);
  if (h) return { duree: { sansArret: false, nombre: h[1].replace('.', ','), unite: 'h' }, brut };
  const j = brut.match(JOURS);
  if (j) return { duree: { sansArret: false, nombre: j[1].replace('.', ','), unite: 'j' }, brut };
  return { duree: null, brut };
}

/** La forme régulière : « sans arrêt », « 2 h », « 1 jour », « 4 jours ». */
export function ecrireDuree(d: Duree): string {
  if (d.sansArret) return 'sans arrêt';
  // « 1, » en cours de frappe s'écrit « 1 » : la virgule orpheline ne se stocke pas.
  const n = d.nombre.trim().replace('.', ',').replace(/,$/, '');
  if (!n) return '';
  if (d.unite === 'h') return `${n} h`;
  return `${n} ${Number(n.replace(',', '.')) >= 2 ? 'jours' : 'jour'}`;
}

/* ─────────────────────────────── Pièces changées ───────────────────────── */

export const AUCUNE_PIECE = 'Aucune';
const RIEN = /^(non|aucune?|rien|n[ée]ant|-|0)$/i;
const SEPARATEURS = /\s*(?:[,;+\n]|\s-\s)\s*/;

/** Clé de comparaison : sans casse, sans accents, espaces resserrés. */
export function clePiece(p: string): string {
  return p.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().replace(/\s+/g, ' ').trim();
}

export function lirePieces(texte: string | null | undefined): { aucune: boolean; pieces: string[] } {
  const brut = String(texte ?? '').trim();
  if (!brut) return { aucune: false, pieces: [] };
  if (RIEN.test(brut)) return { aucune: true, pieces: [] };
  const pieces: string[] = [];
  for (const p of brut.split(SEPARATEURS).map((x) => x.trim()).filter((x) => x && !RIEN.test(x))) {
    if (!pieces.some((q) => clePiece(q) === clePiece(p))) pieces.push(p);
  }
  return { aucune: false, pieces };
}

export function ecrirePieces(aucune: boolean, pieces: string[]): string {
  if (aucune) return AUCUNE_PIECE;
  return pieces.map((p) => p.trim()).filter(Boolean).join(', ');
}

/** Les pièces déjà changées sur l'appareil, des plus fréquentes aux plus rares,
 *  chacune sous la forme la plus souvent écrite (« membrane de ref » ×4 et
 *  « Membrane de référence » ×2 restent deux pièces : on ne devine pas une
 *  abréviation — la liste les montre côte à côte, on choisit). */
export function piecesConnues(textes: (string | null | undefined)[]): string[] {
  const parCle = new Map<string, { n: number; formes: Map<string, number> }>();
  for (const t of textes) {
    for (const p of lirePieces(t).pieces) {
      const k = clePiece(p);
      const e = parCle.get(k) ?? { n: 0, formes: new Map<string, number>() };
      e.n += 1;
      e.formes.set(p, (e.formes.get(p) ?? 0) + 1);
      parCle.set(k, e);
    }
  }
  return [...parCle.values()]
    .sort((a, b) => b.n - a.n)
    .map((e) => [...e.formes.entries()].sort((a, b) => b[1] - a[1])[0][0]);
}

/* ──────────────────── Vérification avant remise en service ─────────────── */

/** Les vérifications d'usage, cochées ; le reste s'écrit en précision. */
export const VERIFICATIONS = [
  'CQI passés et conformes',
  'Calibration refaite',
  'Échantillons patients repassés',
  'Contrôle visuel et fonctionnel',
] as const;
const SEP_VERIF = ' · ';

export function lireVerification(texte: string | null | undefined): { cochees: string[]; precision: string } {
  const brut = String(texte ?? '').trim();
  if (!brut) return { cochees: [], precision: '' };
  const morceaux = brut.split(SEP_VERIF.trim()).map((x) => x.trim()).filter(Boolean);
  const cochees: string[] = [];
  const reste: string[] = [];
  for (const m of morceaux) {
    const v = VERIFICATIONS.find((x) => clePiece(x) === clePiece(m));
    if (v) { if (!cochees.includes(v)) cochees.push(v); } else reste.push(m);
  }
  return { cochees: VERIFICATIONS.filter((v) => cochees.includes(v)), precision: reste.join(SEP_VERIF) };
}

export function ecrireVerification(cochees: readonly string[], precision: string): string {
  return [...VERIFICATIONS.filter((v) => cochees.includes(v)), precision.trim()].filter(Boolean).join(SEP_VERIF);
}
